import { AppRoutes } from './enums';

/**
 * Page title mappings for all routes
 * Format: "Descriptive Title | Bibliotheca"
 */
export const PAGE_TITLES = {
  // Authentication & Public Pages
  [AppRoutes.VALIDATE_EMAIL]: 'Welcome',
  [AppRoutes.LOGIN]: 'Login',
  [AppRoutes.FORGOT_PASSWORD]: 'Forgot Password',
  [AppRoutes.RESET_PASSWORD]: 'Reset Password',
  [AppRoutes.CREATE_PASSWORD]: 'Create Password',

  // Dashboard & Main Pages
  [AppRoutes.USER_DASHBOARD]: 'Dashboard',
  
  // Organization Management
  [AppRoutes.ORGANISATION_OVERVIEW]: 'Organization Overview',
  [AppRoutes.ORGANISATION_LIST]: 'Organizations',
  [AppRoutes.ORGANISATION_DETAILS]: 'Organization Details',
  [AppRoutes.ADD_ORGANISATION]: 'Add Organization',
  [AppRoutes.ORGANISATION_USERS]: 'Organization Users',
  [AppRoutes.ORGANISATION_ADD_NEW_USER]: 'Add New User',
  [AppRoutes.ORGANISATION_USER_PROFILE]: 'User Profile',

  // Settings
  [AppRoutes.GENERAL_SETTINGS]: 'General Settings',
  [AppRoutes.ORGANISATION_LOCATIONS]: 'Location Settings',
  
  // Device Management
  [AppRoutes.DEVICE_SETTINGS]: 'Device Settings',
  [AppRoutes.CREATE_DEVICE]: 'Add Device',
  [AppRoutes.DEVICE_INFO]: 'Device Information',
  [AppRoutes.DEVICE_LIST]: 'Devices',
  [AppRoutes.DEVICE_HEALTH]: 'Device Health',
  [AppRoutes.DEVICE_COMPONENTS]: 'Device Components',
  [AppRoutes.DEVICE_CONFIGURATION]: 'Device Configuration',

  // User Management
  [AppRoutes.USER_LIST]: 'Users',
  [AppRoutes.ADD_NEW_USER]: 'Add New User',
  [AppRoutes.USER_PROFILE]: 'User Profile',
  [AppRoutes.CHANGE_PASSWORD]: 'Change Password',

  // Role Management
  [AppRoutes.ROLES_LIST]: 'Roles',
  [AppRoutes.ROLES_DETAILS]: 'Role Details',
  [AppRoutes.ADD_ROLES]: 'Add Role',

  // Account Settings
  [AppRoutes.ACCOUNT_SETTINGS_ACCOUNT]: 'Account Settings',
  [AppRoutes.ACCOUNT_SETTINGS_GENERAL]: 'General Settings',
  [AppRoutes.ACCOUNT_SETTINGS_NOTIFICATIONS]: 'Notification Settings',
  [AppRoutes.ACCOUNT_SETTINGS_SUGGESTED_PROMPTS]: 'Suggested Prompts',

  // Logs & Monitoring
  [AppRoutes.EVENT_LOGS]: 'Event Logs',
  [AppRoutes.EVENT_LOGS_OUTPUT]: 'Event Details',
  [AppRoutes.AUDIT_LOGS]: 'Audit Logs',

  // Alerts
  [AppRoutes.ALERT_CENTRE]: 'Alert Centre',
  [AppRoutes.ALERT_CONFIG_SETTINGS]: 'Alert Settings',

  // Insights & Analytics
  [AppRoutes.INSIGHTS]: 'Insights',
  [AppRoutes.MY_INSIGHTS]: 'My Insights',
  [AppRoutes.MY_SAVED_INSIGHT]: 'Saved Insight',
  [AppRoutes.MY_SAVED_INSIGHT_EDIT]: 'Edit Insight',

  // Chat & AI
  [AppRoutes.CHAT]: 'AI Chat',

  // Work Items
  [AppRoutes.WORK_ITEMS]: 'Work Items',
  [AppRoutes.MY_WORK_ITEMS]: 'My Work Items',

  // Logout
  [AppRoutes.LOGOUT]: 'Logout',
} as const;

/**
 * Get page title by route
 */
export const getPageTitle = (route: string): string | undefined => {
  // Handle dynamic routes (with parameters)
  const routeKey = Object.keys(PAGE_TITLES).find(key => {
    if (key.includes(':')) {
      // Convert route pattern to regex
      const pattern = key.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(route);
    }
    return key === route;
  });

  return routeKey ? PAGE_TITLES[routeKey as keyof typeof PAGE_TITLES] : undefined;
};

/**
 * Internationalized page titles (using translation keys)
 * These can be used with react-i18next for multilingual support
 */
export const I18N_PAGE_TITLES = {
  // Authentication & Public Pages
  [AppRoutes.VALIDATE_EMAIL]: 'page.welcome',
  [AppRoutes.LOGIN]: 'page.login',
  [AppRoutes.FORGOT_PASSWORD]: 'page.forgotPassword',
  [AppRoutes.RESET_PASSWORD]: 'page.resetPassword',
  [AppRoutes.CREATE_PASSWORD]: 'page.createPassword',

  // Dashboard & Main Pages
  [AppRoutes.USER_DASHBOARD]: 'page.dashboard',
  
  // Organization Management
  [AppRoutes.ORGANISATION_OVERVIEW]: 'page.organizationOverview',
  [AppRoutes.ORGANISATION_LIST]: 'page.organizations',
  [AppRoutes.ORGANISATION_DETAILS]: 'page.organizationDetails',
  [AppRoutes.ADD_ORGANISATION]: 'page.addOrganization',
  [AppRoutes.ORGANISATION_USERS]: 'page.organizationUsers',
  [AppRoutes.ORGANISATION_ADD_NEW_USER]: 'page.addNewUser',
  [AppRoutes.ORGANISATION_USER_PROFILE]: 'page.userProfile',

  // Settings
  [AppRoutes.GENERAL_SETTINGS]: 'page.generalSettings',
  [AppRoutes.ORGANISATION_LOCATIONS]: 'page.locationSettings',
  
  // Device Management
  [AppRoutes.DEVICE_SETTINGS]: 'page.deviceSettings',
  [AppRoutes.CREATE_DEVICE]: 'page.addDevice',
  [AppRoutes.DEVICE_INFO]: 'page.deviceInformation',
  [AppRoutes.DEVICE_LIST]: 'page.devices',
  [AppRoutes.DEVICE_HEALTH]: 'page.deviceHealth',
  [AppRoutes.DEVICE_COMPONENTS]: 'page.deviceComponents',
  [AppRoutes.DEVICE_CONFIGURATION]: 'page.deviceConfiguration',

  // User Management
  [AppRoutes.USER_LIST]: 'page.users',
  [AppRoutes.ADD_NEW_USER]: 'page.addNewUser',
  [AppRoutes.USER_PROFILE]: 'page.userProfile',
  [AppRoutes.CHANGE_PASSWORD]: 'page.changePassword',

  // Role Management
  [AppRoutes.ROLES_LIST]: 'page.roles',
  [AppRoutes.ROLES_DETAILS]: 'page.roleDetails',
  [AppRoutes.ADD_ROLES]: 'page.addRole',

  // Account Settings
  [AppRoutes.ACCOUNT_SETTINGS_ACCOUNT]: 'page.accountSettings',
  [AppRoutes.ACCOUNT_SETTINGS_GENERAL]: 'page.generalSettings',
  [AppRoutes.ACCOUNT_SETTINGS_NOTIFICATIONS]: 'page.notificationSettings',

  // Logs & Monitoring
  [AppRoutes.EVENT_LOGS]: 'page.eventLogs',
  [AppRoutes.EVENT_LOGS_OUTPUT]: 'page.eventDetails',
  [AppRoutes.AUDIT_LOGS]: 'page.auditLogs',

  // Alerts
  [AppRoutes.ALERT_CENTRE]: 'page.alertCentre',
  [AppRoutes.ALERT_CONFIG_SETTINGS]: 'page.alertSettings',

  // Insights & Analytics
  [AppRoutes.INSIGHTS]: 'page.insights',
  [AppRoutes.MY_INSIGHTS]: 'page.myInsights',
  [AppRoutes.MY_SAVED_INSIGHT]: 'page.savedInsight',
  [AppRoutes.MY_SAVED_INSIGHT_EDIT]: 'page.editInsight',

  // Chat & AI
  [AppRoutes.CHAT]: 'page.aiChat',

  // Work Items
  [AppRoutes.WORK_ITEMS]: 'page.workItems',
  [AppRoutes.MY_WORK_ITEMS]: 'page.myWorkItems',

  // Logout
  [AppRoutes.LOGOUT]: 'page.logout',
} as const;

/**
 * Get internationalized page title by route
 */
export const getI18nPageTitle = (route: string): string | undefined => {
  // Handle dynamic routes (with parameters)
  const routeKey = Object.keys(I18N_PAGE_TITLES).find(key => {
    if (key.includes(':')) {
      // Convert route pattern to regex
      const pattern = key.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(route);
    }
    return key === route;
  });

  return routeKey ? I18N_PAGE_TITLES[routeKey as keyof typeof I18N_PAGE_TITLES] : undefined;
};
